import { Button, Upload, message, Popconfirm, Modal, Tooltip } from 'antd';
import { UploadOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { t } from '@/utils/translator';
import api from '@/api/api';
import { captureException } from '@sentry/react';

const AttachmentCell = ({ attachments, record, documentType, refreshCycleData }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const _has_attachments = attachments && Array.isArray(attachments) && attachments.length > 0;

  // 업로드 버튼 활성화 조건: STARTED 또는 UPLOADING 상태일 때만 활성화
  const isUploadEnabled = ['STARTED', 'UPLOADING'].includes(record.cycle_status);

  // 업로드 버튼 표시 조건: REVIEWING, FEEDBACK, DONE 상태가 아닐 때 표시
  const shouldShowUploadButton = !['REVIEWING', 'FEEDBACK', 'DONE'].includes(record.cycle_status);

  // 파일 업로드 핸들러
  const handleUpload = async (file) => {
    // 파일 크기 체크 (300MB 제한)
    const maxSize = 300 * 1024 * 1024; // 300MB in bytes
    if (file.size > maxSize) {
      message.error(t('파일 크기는 300MB를 초과할 수 없습니다'));
      return false;
    }

    // 필수 정보 체크
    if (!documentType || !record.estimate_site_cycle_id) {
      message.error(t('업로드에 필요한 정보가 부족합니다'));
      return false;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_cycle_id', record.estimate_site_cycle_id);
      formData.append('document_type', documentType);

      const response = await api.post('/attachments/site/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000, // 10분 타임아웃
      });

      if (response.data) {
        message.success(t('파일이 성공적으로 업로드되었습니다'));
        // 사이클 데이터만 새로고침
        if (refreshCycleData) {
          setTimeout(() => {
            refreshCycleData();
          }, 500);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);

      let errorMessage = t('파일 업로드 중 오류가 발생했습니다');
      if (error.code === 'ECONNABORTED') {
        errorMessage = t('업로드 시간이 초과되었습니다. 파일 크기를 확인해주세요');
      } else if (error.response?.status === 413) {
        errorMessage = t('파일 크기가 너무 큽니다');
      } else if (error.response?.status === 415) {
        errorMessage = t('지원되지 않는 파일 형식입니다');
      } else if (error.response?.data?.detail) {
        errorMessage = t(error.response.data.detail);
      }

      message.error(errorMessage);
      captureException(error, {
        tags: {
          component: 'AttachmentCell',
          action: 'handleUpload',
        },
        extra: {
          documentType,
          siteId: record.estimate_site_cycle_id,
          fileName: file.name,
          fileSize: file.size,
        },
      });
    } finally {
      setUploading(false);
    }

    return false; // Antd Upload 컴포넌트의 자동 업로드 방지
  };

  // 파일 삭제 핸들러
  const handleDelete = async (attachmentId) => {
    setDeleting(attachmentId);
    setShowDeleteModal(true);

    try {
      await api.delete(`/attachments/attachment/${attachmentId}`);
      message.success(t('파일이 성공적으로 삭제되었습니다'));

      // 사이클 데이터 새로고침
      if (refreshCycleData) {
        setTimeout(() => {
          refreshCycleData();
        }, 500);
      }
    } catch (error) {
      console.error('Delete error:', error);
      let errorMessage = t('파일 삭제 중 오류가 발생했습니다');

      if (error.response?.status === 404) {
        errorMessage = t('파일을 찾을 수 없습니다');
      } else if (error.response?.status === 403) {
        errorMessage = t('파일을 삭제할 권한이 없습니다');
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      message.error(errorMessage);
    } finally {
      setDeleting(null);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      {_has_attachments ? (
        <div
          className={clsx(
            'd-flex flex-column align-items-center justify-content-center text-center',
            shouldShowUploadButton && 'mb-2'
          )}
        >
          {attachments.map((attachment, attch_idx) => {
            return (
              <div
                key={`attch-${attachment.document_type_id}-${attachment.attachment_id || attch_idx}`}
                className="w-100 text-center mb-1"
              >
                <div className="d-inline-flex text-ellipsis" style={{ width: '90%' }}>
                  <a
                    href={`${import.meta.env.VITE_STATIC_URL}/${attachment?.s3_key || ''}`}
                    className="text-truncate"
                    target="_blank"
                    title={attachment?.filename || t('첨부파일')}
                    download
                  >
                    {attachment?.filename || t('첨부파일')}
                  </a>
                </div>
                {isUploadEnabled && (
                  <Popconfirm
                    title={t('파일을 삭제하시겠습니까?')}
                    onConfirm={() => handleDelete(attachment?.attachment_id)}
                    okText={t('삭제')}
                    cancelText={t('취소')}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deleting === attachment?.attachment_id}
                      disabled={deleting !== null}
                      danger
                    />
                  </Popconfirm>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {shouldShowUploadButton && (
        <Upload
          showUploadList={false}
          beforeUpload={handleUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg,.png"
        >
          <Tooltip
            title={t(
              '업로드 가능한 파일 형식: PDF, DOC, DOCX, XLS, XLSX, XLSM, JPG, JPEG, PNG (최대 300MB)'
            )}
            placement="bottom"
          >
            <Button
              size="small"
              icon={<UploadOutlined />}
              disabled={!isUploadEnabled || uploading}
              loading={uploading}
            >
              {uploading ? t('업로드 중...') : t('첨부하기')}
            </Button>
          </Tooltip>
        </Upload>
      )}

      {/* 첨부파일이 없고 업로드 버튼도 표시되지 않을 때 대시 표시 */}
      {!_has_attachments && !shouldShowUploadButton && <span className="text-gray-5">-</span>}

      {/* 파일 삭제 중 모달 */}
      <Modal
        open={showDeleteModal}
        closable={false}
        footer={null}
        centered
        maskClosable={false}
        width={300}
        bodyStyle={{
          textAlign: 'center',
          padding: '40px 20px',
        }}
      >
        <div>
          <LoadingOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '20px' }} />
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
            {t('파일 삭제 중')}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>{t('잠시만 기다려 주세요...')}</div>
        </div>
      </Modal>
    </>
  );
};

export default AttachmentCell;
